import { format as formatDate } from "date-fns"
import { useState } from "react"
import { useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"
import { useDebounceValue } from "usehooks-ts"

import {
  useNavigateUpdatedSearchParams,
  useSimpleSearchParamState,
} from "../../../common/utils"
import {
  selectActiveAccountId,
  selectProfile,
} from "../../../pages/users/reducer"
import {
  PAGE_SIZE_SEARCH_PARAM,
  coercePageSize,
  crmPageSizeStorageKey,
  readCrmPageSize,
  resolveCrmPageSize,
  writeCrmPageSize,
} from "./pageSizePreference"

export const SORT_COLUMN_SEARCH_PARAM = "sc"
export const SORT_DIRECTION_SEARCH_PARAM = "sd"

export const useTableSort = function (
  defaultSortColumn = null,
  defaultSortDirection = "asc",
  invertedColumnNames = []
) {
  // can't useSearchParamState here because consecutive setSearchParams only keeps last one
  // using state initializers instead of updating in useEffect avoids multiple page loads
  const [sortColumn, setSortColumn] = useSimpleSearchParamState(
    SORT_COLUMN_SEARCH_PARAM,
    defaultSortColumn
  )
  const [sortDirection, setSortDirection] = useSimpleSearchParamState(
    SORT_DIRECTION_SEARCH_PARAM,
    defaultSortDirection
  )
  const navigateUpdatedSearchParams = useNavigateUpdatedSearchParams()

  function onSortColumnChanged(columnName) {
    let newSortDirection
    if (columnName === sortColumn) {
      newSortDirection = sortDirection === "asc" ? "desc" : "asc"
    } else {
      newSortDirection = invertedColumnNames.includes(columnName)
        ? "desc"
        : "asc"
    }
    setSortColumn(columnName)
    setSortDirection(newSortDirection)
    navigateUpdatedSearchParams({
      [SORT_COLUMN_SEARCH_PARAM]: columnName,
      [SORT_DIRECTION_SEARCH_PARAM]: newSortDirection,
    })
  }

  function setSort(columnName, direction, { updateUrl = true } = {}) {
    setSortColumn(columnName)
    setSortDirection(direction)
    if (updateUrl) {
      navigateUpdatedSearchParams({
        [SORT_COLUMN_SEARCH_PARAM]: columnName,
        [SORT_DIRECTION_SEARCH_PARAM]: direction,
      })
    }
  }

  const sortPrefix = sortDirection === "asc" ? "" : "-"
  const serverSort = sortColumn ? `${sortPrefix}${sortColumn}` : ""

  return { sortColumn, sortDirection, onSortColumnChanged, setSort, serverSort }
}

export const PAGE_INDEX_SEARCH_PARAM = "pi"
export { PAGE_SIZE_OPTIONS, PAGE_SIZE_SEARCH_PARAM } from "./pageSizePreference"

export function usePaginationState(defaultPageSize = 20) {
  const [totalResultsSize, setTotalResultsSize] = useState(0)
  const profile = useSelector(selectProfile)
  const activeAccountId = useSelector(selectActiveAccountId)
  const storageKey = crmPageSizeStorageKey(profile?.id, activeAccountId)
  const searchParams = useSearchParams()[0]
  const storedPageSize = readCrmPageSize(
    typeof window === "undefined" ? null : window.localStorage,
    storageKey
  )
  const urlPageSize = coercePageSize(searchParams.get(PAGE_SIZE_SEARCH_PARAM))
  const initialPageSize = resolveCrmPageSize({
    storedValue: storedPageSize,
    urlValue: urlPageSize,
    defaultPageSize,
  })
  const urlPageIndex =
    parseInt(searchParams.get(PAGE_INDEX_SEARCH_PARAM), 10) || 1
  const resetPageForStoredSize =
    storedPageSize != null &&
    urlPageSize != null &&
    storedPageSize !== urlPageSize
  const [pageIndex, setPageIndex] = useState(
    resetPageForStoredSize ? 1 : urlPageIndex
  )
  const [pageSize, setPageSize] = useState(initialPageSize)
  const navigateUpdatedSearchParams = useNavigateUpdatedSearchParams()

  function updateSearchParams(pageIndex, pageSize) {
    navigateUpdatedSearchParams({
      [PAGE_INDEX_SEARCH_PARAM]: pageIndex,
      [PAGE_SIZE_SEARCH_PARAM]: pageSize,
    })
  }

  function handlePageIndexChanged(
    pageIndex,
    /* used within search text state hook below */ navigate = true
  ) {
    setPageIndex(pageIndex)
    if (navigate) {
      updateSearchParams(pageIndex, pageSize)
    }
  }

  function handlePageSizeChanged(nextPageSize) {
    const size = coercePageSize(nextPageSize) ?? defaultPageSize
    writeCrmPageSize(
      typeof window === "undefined" ? null : window.localStorage,
      storageKey,
      size
    )
    setPageIndex(1)
    setPageSize(size)
    updateSearchParams(1, size)
  }

  return {
    totalResultsSize,
    setTotalResultsSize,
    pageIndex,
    setPageIndex: handlePageIndexChanged,
    pageSize,
    setPageSize: handlePageSizeChanged,
  }
}

export function useTableSearchTextState(paramName = "q", setPageIndex) {
  const [searchText, setSearchText] = useSimpleSearchParamState(paramName, "")
  const debouncedSearchText = useDebounceValue(searchText)[0]
  const navigateUpdatedSearchParams = useNavigateUpdatedSearchParams()

  function updateSearchText(searchText) {
    setSearchText(searchText)
    if (setPageIndex) {
      setPageIndex(1, false)
    }
    navigateUpdatedSearchParams({
      [paramName]: searchText,
      [PAGE_INDEX_SEARCH_PARAM]: 1,
    })
  }

  // The raw setter updates local state only, for callers that write the URL
  // themselves in one navigate (consecutive navigates keep only the last).
  return [searchText, updateSearchText, debouncedSearchText, setSearchText]
}

export function formatTableDate(dateStr) {
  if (dateStr == null || dateStr === "") {
    return null
  }
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return formatDate(date, "MMM d yyyy, HH:mm aa")
}

export function getTableSearchParams(
  searchText,
  pageIndex,
  pageSize,
  serverSort
) {
  return {
    q: searchText,
    page: pageIndex,
    page_size: pageSize,
    ordering: serverSort,
  }
}
